"use client";

import * as React from "react";
import { Search } from "lucide-react";
import { Input } from "../primitives/input";
import { Button } from "../primitives/button";
import { cn } from "../primitives/cn";

export type ListToolbarProps = {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  selectedCount?: number;
  /** Localized “N selected” label; defaults to English. */
  selectedLabel?: string;
  bulkActions?: React.ReactNode;
  filters?: React.ReactNode;
  className?: string;
};

/**
 * ListToolbar — search + filters; switches to bulk actions when rows are selected.
 */
export function ListToolbar({
  search,
  onSearchChange,
  searchPlaceholder = "Search…",
  selectedCount = 0,
  selectedLabel,
  bulkActions,
  filters,
  className,
}: ListToolbarProps) {
  if (selectedCount > 0) {
    return (
      <div
        className={cn(
          "flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/40 px-3 py-2",
          className,
        )}
      >
        <span className="text-sm font-medium">
          {selectedLabel ?? `${selectedCount} selected`}
        </span>
        <div className="flex flex-1 flex-wrap items-center gap-2">{bulkActions}</div>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-3", className)}>
      <div className="relative min-w-[200px] flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className="pl-9"
          aria-label={searchPlaceholder}
        />
      </div>
      {filters}
    </div>
  );
}

export function ListToolbarFilterButton({
  children,
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button type="button" variant="outline" size="sm" {...props}>
      {children}
    </Button>
  );
}
